// onTimeDelivery.js — On-Time Delivery modal (Recent Deliveries only)

import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";

const OTD_WEEKLY_TREND = [
  { week: "Wk 1", pct: 91.2 },
  { week: "Wk 2", pct: 88.5 },
  { week: "Wk 3", pct: 94.0 },
  { week: "Wk 4", pct: 92.7 },
];

const OTD_DETAIL = [
  {
    module: "12WO001234",
    building: "Flatz 480 - Builing 5",
    scheduledDate: "2024-03-10",
    deliveredDate: "2024-03-10",
    status: "On Time",
  },
  {
    module: "12WO001235",
    building: "Flatz 480 - Builing 6",
    scheduledDate: "2024-03-11",
    deliveredDate: "2024-03-12",
    status: "Late",
  },
  {
    module: "12WO001236",
    building: "Flatz 480 - Builing 5",
    scheduledDate: "2024-03-12",
    deliveredDate: "2024-03-14",
    status: "Late",
  },
  {
    module: "12WO001354",
    building: "Flatz 326- PROTO",
    scheduledDate: "2024-03-13",
    deliveredDate: "2024-03-13",
    status: "On Time",
  },
  {
    module: "12WO001356",
    building: "Flatz 480 - Builing 6",
    scheduledDate: "2024-03-14",
    deliveredDate: "2024-03-14",
    status: "On Time",
  },
  {
    module: "12WO001357",
    building: "Flatz 480 - Builing 6",
    scheduledDate: "2024-03-18",
    deliveredDate: "2024-03-18",
    status: "On Time",
  },
  {
    module: "12WO001855",
    building: "Flatz 326- PROTO",
    scheduledDate: "2024-03-19",
    deliveredDate: "2024-03-19",
    status: "On Time",
  },
  {
    module: "12WO001856",
    building: "Flatz 480 - Builing 5",
    scheduledDate: "2024-03-20",
    deliveredDate: "2024-03-21",
    status: "Late",
  },
];

const barColor = (pct) =>
  pct >= 90 ? "#38a169" : pct >= 82 ? "#d69e2e" : "#cc0000";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="pml-chart-tooltip">
      <div className="pml-chart-tooltip-date">{label}</div>
      <div className="pml-chart-tooltip-row">
        <div
          className="pml-chart-tooltip-dot"
          style={{ background: barColor(payload[0].value) }}
        />
        {payload[0].value}% on-time
      </div>
    </div>
  );
};

const OnTimeDelivery = ({ goals }) => {
  const target = goals?.onTimeDelivery || 95.0;
  const onTimeCount = OTD_DETAIL.filter((r) => r.status === "On Time").length;
  const lateCount = OTD_DETAIL.length - onTimeCount;
  const overallPct = ((onTimeCount / OTD_DETAIL.length) * 100).toFixed(1);
  const avgWeekly = (
    OTD_WEEKLY_TREND.reduce((s, w) => s + w.pct, 0) / OTD_WEEKLY_TREND.length
  ).toFixed(1);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="pml-modal-stat-band pml-modal-stat-band--4">
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Overall OTD</span>
          <span
            className={`pml-modal-stat-value pml-modal-stat-value--${parseFloat(overallPct) >= target ? "ok" : "alert"}`}
          >
            {overallPct}%
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Target</span>
          <span className="pml-modal-stat-value">{target}%</span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">On Time</span>
          <span className="pml-modal-stat-value pml-modal-stat-value--ok">
            {onTimeCount}
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Late Deliveries</span>
          <span
            className={`pml-modal-stat-value pml-modal-stat-value--${lateCount === 0 ? "ok" : "alert"}`}
          >
            {lateCount}
          </span>
        </div>
      </div>

      {/* Weekly bar chart */}
      <p className="pml-modal-sub-title">Weekly OTD Trend (Last 4 Weeks)</p>
      <div style={{ height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={OTD_WEEKLY_TREND}
            margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e2e8f0"
              vertical={false}
            />
            <XAxis
              dataKey="week"
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={{ stroke: "#e2e8f0" }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${v}%`}
              domain={[75, 100]}
              width={38}
            />
            <Tooltip content={<CustomTooltip />} />
            {/* Black dotted target line */}
            <ReferenceLine
              y={target}
              stroke="#1a1a1a"
              strokeWidth={1.5}
              strokeDasharray="6 3"
              label={{
                value: `Target ${target}%`,
                position: "insideTopRight",
                fontSize: 11,
                fill: "#1a1a1a",
              }}
            />
            <Bar dataKey="pct" radius={[4, 4, 0, 0]}>
              {OTD_WEEKLY_TREND.map((entry, i) => (
                <Cell key={i} fill={barColor(entry.pct)} fillOpacity={0.85} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Recent Deliveries table only — Performance by Building removed */}
      <p className="pml-modal-sub-title">Recent Deliveries</p>
      <div className="pml-modal-scroll-wrap" style={{ maxHeight: 220 }}>
        <table className="pml-modal-table">
          <thead>
            <tr>
              <th>Building</th>
              <th>Module</th>
              <th>Scheduled</th>
              <th>Delivered</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {OTD_DETAIL.map((row, i) => (
              <tr key={i}>
                <td>{row.building}</td>
                <td style={{ fontWeight: 600 }}>{row.module}</td>
                <td>{row.scheduledDate}</td>
                <td>{row.deliveredDate}</td>
                <td
                  className={
                    row.status === "On Time"
                      ? "pml-variance-cell--pos"
                      : "pml-variance-cell--neg"
                  }
                >
                  {row.status}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 12, color: "#a0aec0" }}>
        4-week average OTD: {avgWeekly}%
      </div>
    </div>
  );
};

export default OnTimeDelivery;
